import { z } from 'zod';

import { logger } from '@modules/logger';

import { GetUserDetailsQuery } from './user-details.graphql';

export enum WorkspaceType {
  INDIVIDUAL = 'INDIVIDUAL',
  TEAM = 'TEAM',
}

const workspaceTypeValidator = z.nativeEnum(WorkspaceType);

export enum WorkspaceRoleEnum {
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
}

const workspaceProfileRoleValidator = z.nativeEnum(WorkspaceRoleEnum);

export enum WorkspacePlanTypeEnum {
  TEAM = 'TEAM',
  PREMIUM = 'PREMIUM',
  FREE = 'FREE',
}

const workspacePlanTypeValidator = z.nativeEnum(WorkspacePlanTypeEnum);

const DEFAULT_MAX_PERSONAL_CARD_LIMIT_FREE_PLAN = 2;

const workspacePlanValidator = z.object({
  is_trialing: z.boolean(),
  type: workspacePlanTypeValidator,
  name: z.string(),
  max_personal_cards: z.number().default(DEFAULT_MAX_PERSONAL_CARD_LIMIT_FREE_PLAN),
});

const workspaceTagsValidator = z
  .object({
    disable_email_signatures: z.boolean().nullable(),
  })
  .nullable();

const workspaceProfileValidator = z.object({
  id: z.string(),
  given_name: z.string().nullable(),
  family_name: z.string().nullable(),
  avatar_url: z.string().nullable(),
  role: workspaceProfileRoleValidator,
});

const currentWorkspaceValidator = z.object({
  id: z.string(),
  name: z.string().nullable(),
  type: workspaceTypeValidator,
  plan: workspacePlanValidator,
  stripe_customer_id: z.string().nullable(),
  avatar_url: z.string().nullable(),
  profile: workspaceProfileValidator,
  tags: workspaceTagsValidator,
});

const userDetailsValidator = z.object({
  id: z.string(),
  email: z.string().nullable(),
  current_workspace: currentWorkspaceValidator,
  individual_stripe_customer_id: z.string().nullable(),
});

export type ValidUserDetails = z.infer<typeof userDetailsValidator>;

// This ensures the schema is defined correctly and in sync with types generated by GraphQL Codegen
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _: GetUserDetailsQuery['user_details'] = {} as ValidUserDetails;

export const validateApolloResponse = (data: GetUserDetailsQuery['user_details']): ValidUserDetails => {
  const parsedResponse = userDetailsValidator.safeParse(data);

  if (!parsedResponse.success) {
    // Log issues for observability
    logger.error(JSON.stringify(parsedResponse.error.issues));
    const errors = parsedResponse.error.flatten();

    if (process.env.NODE_ENV === 'development') {
      throw new Error(JSON.stringify(errors));
    }
    // Try our best with the data when in production
    return data as ValidUserDetails;
  } else {
    return parsedResponse.data;
  }
};
